import { memo, useCallback, useRef, useState, type KeyboardEvent } from 'react';

import { ChevronDownIcon, SparklesIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import useMembers from '@/hooks/useMembers';
import { cn } from '@/lib/utils';
import { formatTimeOnly } from '@/util/dateUtils';

import { ACTIVITY_CARD_CLASS } from './activityCard';

type Props = { summary: string; userId?: string; created: string };

export const SummaryEvent = memo(({ summary, userId, created }: Props) => {
  const { t } = useTranslation();
  const { getMember } = useMembers();
  const [isExpanded, setIsExpanded] = useState(true);
  const cardRef = useRef<HTMLDivElement>(null);

  const user = userId ? getMember(userId) : undefined;

  const handleClick = useCallback(() => {
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && cardRef.current?.contains(selection.anchorNode)) {
      return;
    }
    setIsExpanded((expanded) => !expanded);
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    setIsExpanded((expanded) => !expanded);
  }, []);

  return (
    <div className="flex justify-end px-6">
      <div
        ref={cardRef}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? t('common.collapse') : t('common.expand')}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(ACTIVITY_CARD_CLASS, 'w-md max-w-full cursor-pointer bg-ai-soft')}
      >
        <div className="flex items-baseline gap-1.5 py-0.5 select-none">
          <SparklesIcon size={16} className="shrink-0 self-center text-muted-foreground" />
          <span className="min-w-0 truncate text-sm leading-5 font-medium text-foreground">
            {t('conversation.summary.header')}
          </span>
          <TimestampTooltip timestamp={created}>
            <span className="text-xs leading-4 font-normal text-muted-foreground">
              {formatTimeOnly(created)}
            </span>
          </TimestampTooltip>
          <ChevronDownIcon
            size={16}
            className={cn(
              'ml-auto shrink-0 self-center text-muted-foreground transition-transform duration-200',
              isExpanded && 'rotate-180'
            )}
          />
        </div>

        {isExpanded && (
          <>
            <p className="w-full min-w-0 text-sm leading-6 break-normal whitespace-pre-wrap">
              {summary}
            </p>

            <div className="mt-2 flex items-center gap-1.5">
              <Avatar size="xxs">
                <AvatarImage src={user?.avatar} alt={user?.name} />
                <AvatarFallback size="xxs">{user?.name?.[0]}</AvatarFallback>
              </Avatar>
              <span className="text-xs leading-4 font-medium">{user?.name}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

SummaryEvent.displayName = 'SummaryEvent';
